import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import ScrollReveal from "@/components/ScrollReveal";
import { Layers, Target, Compass, Diamond, Eye, Globe, ArrowRight, Check, Shield } from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/ce9ebb17-b340-4f23-afd3-bab0fd00e609/images/9b85d50c8b3048bc6f2c9c3fa2dfe177656f3a67de4b27540386c61c981b90e7.png";
const PROCESS_IMG = "https://static.prod-images.emergentagent.com/jobs/ce9ebb17-b340-4f23-afd3-bab0fd00e609/images/789f8b17cb0379b02836c691c64a0cba98b6e9fd73e790bb9716ceb0187f304e.png";

const iconMap = { Layers, Target, Compass, Diamond, Eye, Globe };

export default function Home() {
  const { t } = useLanguage();

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1A]/90 via-[#333333]/80 to-[#333333]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 pt-32 pb-24">
          <div className="max-w-3xl">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#E8A400] mb-6 animate-fade-in-up opacity-0 stagger-1">
              {t.hero.overline}
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter text-white animate-fade-in-up opacity-0 stagger-2 leading-[0.95]">
              {t.hero.headline}
            </h1>
            <p className="mt-8 text-base sm:text-lg text-white/60 max-w-xl leading-relaxed animate-fade-in-up opacity-0 stagger-3">
              {t.hero.subheadline}
            </p>
            <div className="mt-10 flex flex-wrap gap-4 animate-fade-in-up opacity-0 stagger-4">
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="bg-[#E8A400] text-[#1A1A1A] font-semibold px-8 py-4 hover:bg-[#C68C00] transition-colors duration-300 rounded-sm flex items-center gap-2 text-sm"
              >
                {t.hero.cta}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-secondary"
                className="border border-white/20 text-white font-medium px-8 py-4 hover:bg-white/5 transition-colors duration-300 rounded-sm text-sm"
              >
                {t.hero.secondaryCta}
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#333333] to-transparent" />
      </section>

      {/* Intro */}
      <section data-testid="intro-section" className="py-24 sm:py-32 bg-[#333333]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <ScrollReveal>
            <div className="max-w-3xl">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#E8A400] mb-6">
                {t.intro.overline}
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
                {t.intro.title}
              </h2>
              <p className="mt-6 text-base text-white/60 leading-relaxed max-w-2xl">
                {t.intro.text}
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Services Overview */}
      <section data-testid="services-overview" className="py-24 sm:py-32 bg-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <ScrollReveal>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#E8A400] mb-6">
              {t.servicesOverview.overline}
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-16">
              {t.servicesOverview.title}
            </h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-3 border border-white/10">
            {t.servicesOverview.services.map((service, i) => {
              const Icon = iconMap[service.icon] || Layers;
              return (
                <ScrollReveal key={i} delay={i * 100}>
                  <div
                    data-testid={`service-card-${i}`}
                    className={`p-8 lg:p-10 ${
                      i < 2 ? "border-b md:border-b-0 md:border-r border-white/10" : ""
                    } hover:bg-white/[0.02] transition-colors duration-500 group h-full`}
                  >
                    <div className="w-12 h-12 rounded-sm bg-[#E8A400]/10 flex items-center justify-center mb-6 group-hover:bg-[#E8A400]/20 transition-colors duration-500">
                      <Icon className="w-6 h-6 text-[#E8A400]" />
                    </div>
                    <h3 className="text-xl font-semibold tracking-tight mb-4">
                      {service.title}
                    </h3>
                    <p className="text-sm text-white/50 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
          <ScrollReveal delay={400}>
            <div className="mt-12 text-center">
              <Link
                to="/services"
                data-testid="services-overview-cta"
                className="inline-flex items-center gap-2 text-sm font-medium text-[#E8A400] hover:text-[#C68C00] transition-colors duration-300"
              >
                {t.servicesOverview.cta}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Why Choose Us */}
      <section data-testid="why-choose-us" className="py-24 sm:py-32 bg-[#333333]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <ScrollReveal>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#E8A400] mb-6">
              {t.whyChooseUs.overline}
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-16">
              {t.whyChooseUs.title}
            </h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
            {t.whyChooseUs.points.map((point, i) => {
              const Icon = iconMap[point.icon] || Layers;
              return (
                <ScrollReveal key={i} delay={i * 100}>
                  <div
                    data-testid={`why-point-${i}`}
                    className="bg-[#333333] p-8 lg:p-10 group"
                  >
                    <div className="flex items-start gap-5">
                      <div className="w-10 h-10 rounded-sm bg-[#E8A400]/10 flex items-center justify-center shrink-0 group-hover:bg-[#E8A400]/20 transition-colors">
                        <Icon className="w-5 h-5 text-[#E8A400]" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold tracking-tight mb-2">
                          {point.title}
                        </h3>
                        <p className="text-sm text-white/50 leading-relaxed">
                          {point.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section data-testid="process-section" className="relative py-24 sm:py-32">
        <div className="absolute inset-0">
          <img src={PROCESS_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1A1A1A]/90" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <ScrollReveal>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#E8A400] mb-6">
              {t.process.overline}
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-16">
              {t.process.title}
            </h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {t.process.steps.map((step, i) => (
              <ScrollReveal key={i} delay={i * 120}>
                <div data-testid={`process-step-${i}`} className="text-left">
                  <span className="text-5xl font-bold tracking-tighter text-[#E8A400]/30 block mb-4">
                    {step.number}
                  </span>
                  <h3 className="text-lg font-semibold tracking-tight mb-3">
                    {step.title}
                  </h3>
                  <p className="text-sm text-white/50 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Trust / Guarantee */}
      <section data-testid="trust-section" className="py-24 sm:py-32 bg-[#333333]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
          <ScrollReveal>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#E8A400] mb-6">
              {t.trust.overline}
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-12">
              {t.trust.title}
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={100}>
            <div className="bg-[#2A2A2A] border border-white/10 p-8 md:p-12 max-w-3xl">
              <div className="flex items-center gap-3 mb-6">
                <Shield className="w-6 h-6 text-[#E8A400]" />
                <h3 data-testid="guarantee-title" className="text-xl sm:text-2xl font-semibold tracking-tight">
                  {t.trust.guaranteeTitle}
                </h3>
              </div>
              <p className="text-white/60 leading-relaxed mb-8 text-sm sm:text-base">
                {t.trust.guaranteeText}
              </p>
              <ul className="space-y-3">
                {t.trust.points.map((point, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#E8A400] mt-0.5 shrink-0" />
                    <span className="text-sm text-white/70">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Final CTA */}
      <section data-testid="final-cta" className="py-24 sm:py-32 bg-[#2A2A2A]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24 text-center">
          <ScrollReveal>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter mb-6">
              {t.finalCta.title}
            </h2>
            <p className="text-base text-white/60 max-w-lg mx-auto mb-10">
              {t.finalCta.description}
            </p>
            <Link
              to="/contact"
              data-testid="final-cta-btn"
              className="inline-flex items-center gap-2 bg-[#E8A400] text-[#1A1A1A] font-semibold px-10 py-4 hover:bg-[#C68C00] transition-colors duration-300 rounded-sm text-sm"
            >
              {t.finalCta.cta}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
